import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsApi } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Bell, RefreshCw, CheckCheck, LogOut, BellRing, Home } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';
import { PushToggle } from '../PushNotification';

const NOTIF_ICONS = {
  order_created: '📦',
  order_dispatched: '🚚',
  deadline_approaching: '⏰',
  mention: '💬',
  receipt: '📥',
};

const Header = ({ title, children, actions, onRefresh }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const popupChecked = useRef(false);

  useEffect(() => {
    fetchNotifications();
    checkDeadlines();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Show popup toasts for unseen notifications (once per session)
  useEffect(() => {
    if (!popupChecked.current && user) {
      popupChecked.current = true;
      showPopupNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data.slice(0, 10));
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const showPopupNotifications = async () => {
    try {
      const { data } = await notificationsApi.getPopups();
      if (data.length > 0) {
        data.slice(0, 5).forEach((n, i) => {
          setTimeout(() => {
            const icon = NOTIF_ICONS[n.type] || '🔔';
            toast(n.title, {
              description: n.message,
              icon,
              duration: 6000,
            });
          }, i * 800);
        });
        await notificationsApi.dismissPopups();
      }
    } catch { /* silent */ }
  };

  const checkDeadlines = async () => {
    try {
      await notificationsApi.checkDeadlines();
    } catch { /* silent */ }
  };

  const markAsRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      fetchNotifications();
    } catch { /* silent */ }
  };

  return (
    <header className="sticky top-0 z-30 h-14 md:h-16 border-b border-slate-200/60 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/80 backdrop-blur-xl">
      <div className="flex items-center justify-between h-full px-3 md:px-6">
        <div className="flex items-center gap-2 md:gap-4">
          {/* Home button — takes user back to the Main Menu */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white shadow-md shadow-amber-500/30 transition-all hover:shadow-lg hover:scale-105 active:scale-95 -ml-1"
            data-testid="header-home-btn"
            title="Back to Main Menu"
          >
            <Home className="h-4.5 w-4.5" strokeWidth={2.5} />
          </button>
          <h1 className="font-heading text-base md:text-xl font-bold text-slate-900 dark:text-white truncate max-w-[140px] md:max-w-none">
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-1.5 md:gap-3">
          {/* Actions (buttons, etc.) */}
          {actions}

          {/* Children (filters, etc.) - hidden on small mobile */}
          <div className="hidden sm:flex items-center gap-2 md:gap-3">
            {children}
          </div>

          {onRefresh && (
            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              data-testid="refresh-btn"
              className="h-8 w-8 md:h-9 md:w-9"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}

          {/* Notifications dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative h-8 w-8 md:h-9 md:w-9"
                data-testid="notifications-dropdown"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 h-4 w-4 md:h-5 md:w-5 flex items-center justify-center p-0 bg-amber-500 text-white text-[10px] md:text-xs"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 md:w-80">
              <div className="flex items-center justify-between px-2">
                <DropdownMenuLabel className="font-heading">Notifications</DropdownMenuLabel>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:text-amber-700" onClick={markAllRead} data-testid="mark-all-read-btn">
                    <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-500">
                  No notifications
                </div>
              ) : (
                notifications.map((notification) => (
                  <DropdownMenuItem
                    key={notification.id}
                    className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-base flex-shrink-0">{NOTIF_ICONS[notification.type] || '🔔'}</span>
                      <span className={`font-medium text-sm ${notification.is_read ? 'text-slate-500' : 'text-slate-900 dark:text-white'}`}>
                        {notification.title}
                      </span>
                      {!notification.is_read && (
                        <span className="h-2 w-2 rounded-full bg-amber-500 ml-auto flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 pl-7">
                      {notification.message}
                    </p>
                    <span className="text-xs text-slate-400 pl-7">
                      {formatDateTime(notification.created_at)}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu with sign out */}
          <PushToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-3 border-l border-slate-200 dark:border-neutral-800 cursor-pointer hover:opacity-80 transition-opacity" data-testid="user-menu-btn">
                <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-600 dark:text-amber-400 font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-sm hidden lg:block">
                  <p className="font-medium text-slate-900 dark:text-white">{user?.name}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.role}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 dark:text-red-400 cursor-pointer" data-testid="signout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
